import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateUserAttributes } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';

export default function Profile() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUserAttributes({
        userAttributes: {
          given_name: form.firstName,
          family_name: form.lastName,
        },
      });
      await checkAuth(); // Refresh user context
      setSuccess('Profile updated successfully');
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 space-y-6">
      {/* Profile Info */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">My Profile</h2>
            {!editing && (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit</Button>
            )}
          </div>

          {error && <Alert variant="error" className="mb-4" onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" className="mb-4" onClose={() => setSuccess('')}>{success}</Alert>}

          {editing ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  value={form.firstName}
                  onChange={(e) => setForm(p => ({ ...p, firstName: e.target.value }))}
                />
                <Input
                  label="Last Name"
                  value={form.lastName}
                  onChange={(e) => setForm(p => ({ ...p, lastName: e.target.value }))}
                />
              </div>
              <Input label="Email" value={form.email} disabled />
              <p className="text-xs text-gray-400">Email cannot be changed. Contact support if needed.</p>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button type="submit" loading={loading}>Save Changes</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary-700">
                    {user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'User'}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">First Name</p>
                  <p className="text-sm font-medium">{user?.firstName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Last Name</p>
                  <p className="text-sm font-medium">{user?.lastName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium">{user?.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Role</p>
                  <p className="text-sm font-medium capitalize">{user?.role || 'Admin'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tenant</p>
                  <p className="text-sm font-medium">{user?.tenantId || 'default'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">User ID</p>
                  <p className="text-sm font-medium font-mono text-xs">{user?.id || '—'}</p>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Security */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Security</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Password</p>
              <p className="text-xs text-gray-500">Last changed: Unknown</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate('/change-password')}>
              Change Password
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Danger Zone */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-red-600 mb-2">Danger Zone</h3>
          <p className="text-sm text-gray-500 mb-4">These actions are irreversible.</p>
          <Button variant="danger" size="sm" disabled>Delete Account</Button>
          <p className="text-xs text-gray-400 mt-1">Contact support to delete your account.</p>
        </CardBody>
      </Card>
    </div>
  );
}
